import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LOADING_MESSAGES = [
    "Scanning markets...",
    "Pulling real-time prediction data...",
    "Analyzing liquidity...",
    "Tracking volume spikes...",
    "Evaluating profit probability..."
];

const MARKET_CARDS = [
    "ETH > $3000 by 2026?",
    "BTC hits $100k this year?",
    "AI replaces 50% jobs?",
    "Mars landing before 2030?",
    "Quantum computing breakthrough?",
    "Electric vehicles dominate?",
    "Nuclear fusion goes live?",
    "Universal basic income passed?",
    "Trump wins 2024?",
    "Crypto regulation imminent?",
    "Web3 mainstream adoption?",
    "Climate goals achieved?"
];

export const ConnectionLoader = ({ onComplete }) => {
    const [msgIndex, setMsgIndex] = useState(0);
    const [phase, setPhase] = useState(1); // Phase 1 or 2
    const [flickerCard, setFlickerCard] = useState(0);
    const [showFinalCard, setShowFinalCard] = useState(false);

    useEffect(() => {
        // Phase 1: Logo + Tagline + Micro-text (2 seconds)
        const msgInterval = setInterval(() => {
            setMsgIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
        }, 600);

        // Switch to Phase 2 after 2 seconds
        const phaseTimeout = setTimeout(() => {
            setPhase(2);
        }, 2000);

        return () => {
            clearInterval(msgInterval);
            clearTimeout(phaseTimeout);
        };
    }, []);

    useEffect(() => {
        if (phase === 2) {
            // Phase 2: Rapid card flicker (300ms each, 10 cards = 3s)
            let cardCount = 0;
            const flickerInterval = setInterval(() => {
                if (cardCount < 10) {
                    setFlickerCard((prev) => (prev + 1) % MARKET_CARDS.length);
                    cardCount++;
                } else {
                    clearInterval(flickerInterval);
                    setShowFinalCard(true);
                    // Hold final card for 500ms then complete
                    setTimeout(() => {
                        if (onComplete) onComplete();
                    }, 800);
                }
            }, 300);

            return () => clearInterval(flickerInterval);
        }
    }, [phase, onComplete]);

    return (
        <div className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center overflow-hidden">
            {/* Background Neural Effect with Data Streams */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
                {/* AI Core Glow */}
                <motion.div
                    animate={
                        phase === 2
                            ? { scale: [1, 1.5, 1], opacity: [0.3, 0.8, 0.3] }
                            : { scale: [1, 1.2, 1], opacity: [0.2, 0.4, 0.2] }
                    }
                    transition={{ duration: phase === 2 ? 2 : 4, repeat: Infinity, ease: "linear" }}
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/20 rounded-full blur-[100px]"
                />

                {/* Data Streams (Phase 2) */}
                {phase === 2 && (
                    <>
                        {[...Array(8)].map((_, i) => (
                            <motion.div
                                key={i}
                                initial={{ x: -100, opacity: 0 }}
                                animate={{ x: window.innerWidth + 100, opacity: [0, 1, 0] }}
                                transition={{
                                    duration: 2,
                                    delay: i * 0.3,
                                    repeat: Infinity,
                                    ease: "linear"
                                }}
                                className="absolute w-64 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent"
                                style={{ top: `${15 + i * 10}%`, left: 0 }}
                            />
                        ))}
                    </>
                )}
            </div>

            <div className="relative z-10 flex flex-col items-center gap-8 max-w-md w-full px-6 text-center">
                {/* Phase 1: Logo + Branding */}
                <AnimatePresence>
                    {phase === 1 && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.5 }}
                            className="flex flex-col items-center gap-4"
                        >
                            <div className="relative w-20 h-20">
                                <div className="absolute inset-0 border-2 border-primary/30 rounded-full animate-[spin_3s_linear_infinite]" />
                                <div className="absolute inset-2 border-2 border-secondary/30 rounded-full animate-[spin_4s_linear_infinite_reverse]" />
                                <img
                                    src="/claw_logo.png"
                                    alt="PredictClaw"
                                    className="absolute inset-4 w-[calc(100%-32px)] h-[calc(100%-32px)] object-contain rounded-full"
                                    onError={(e) => e.target.style.display = 'none'}
                                />
                            </div>

                            {/* Typewriter Tagline */}
                            <div>
                                <motion.h1
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.5, delay: 0.3 }}
                                    className="text-2xl font-display font-black tracking-tighter text-white uppercase mb-1"
                                >
                                    PredictClaw
                                </motion.h1>
                                <motion.p
                                    initial={{ opacity: 0, width: 0 }}
                                    animate={{ opacity: 1, width: "auto" }}
                                    transition={{ duration: 1, delay: 0.5 }}
                                    className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold overflow-hidden whitespace-nowrap"
                                >
                                    OpenClaw Powered Prediction Intelligence
                                </motion.p>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Phase 2: Rapid Card Flicker */}
                <AnimatePresence>
                    {phase === 2 && !showFinalCard && (
                        <motion.div
                            key={flickerCard}
                            initial={{ opacity: 0, scale: 0.95, rotateX: -15 }}
                            animate={{ opacity: 1, scale: 1, rotateX: 0 }}
                            exit={{ opacity: 0, scale: 0.95, rotateX: 15 }}
                            transition={{ duration: 0.15 }}
                            className="relative w-full max-w-sm p-6 rounded-xl bg-gradient-to-br from-white/10 to-white/5 border border-white/20 backdrop-blur-md shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-mono text-primary uppercase tracking-wider">Analyzing</span>
                                <div className="flex gap-1">
                                    {[...Array(3)].map((_, i) => (
                                        <div key={i} className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" style={{ animationDelay: `${i * 0.2}s` }} />
                                    ))}
                                </div>
                            </div>
                            <p className="text-lg font-bold text-white">{MARKET_CARDS[flickerCard]}</p>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Final Card with Glitch + Glow */}
                <AnimatePresence>
                    {showFinalCard && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, filter: "blur(10px)" }}
                            animate={{
                                opacity: 1,
                                scale: 1,
                                filter: "blur(0px)",
                                boxShadow: [
                                    "0 0 20px rgba(57, 230, 106, 0.3)",
                                    "0 0 60px rgba(57, 230, 106, 0.6)",
                                    "0 0 20px rgba(57, 230, 106, 0.3)"
                                ]
                            }}
                            transition={{ duration: 0.5, boxShadow: { duration: 1.5, repeat: Infinity } }}
                            className="relative w-full max-w-sm p-8 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 border-2 border-primary/50 backdrop-blur-md"
                        >
                            <div className="absolute inset-0 bg-primary/10 rounded-xl animate-pulse" />
                            <div className="relative">
                                <p className="text-sm font-mono text-secondary uppercase tracking-wider mb-2">Connection Established</p>
                                <p className="text-2xl font-black text-white mb-2">Intelligence Online</p>
                                <p className="text-xs text-muted-foreground">Ready to analyze markets...</p>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Phase 1: Loading Bar & Micro-text */}
                {phase === 1 && (
                    <div className="w-full space-y-4">
                        <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden">
                            <motion.div
                                initial={{ width: "0%" }}
                                animate={{ width: "100%" }}
                                transition={{ duration: 1.8, ease: "easeInOut" }}
                                className="h-full bg-gradient-to-r from-primary via-white to-secondary"
                            />
                        </div>

                        <div className="h-6 overflow-hidden relative">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={msgIndex}
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    exit={{ y: -20, opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="absolute inset-0 flex items-center justify-center"
                                >
                                    <span className="text-sm font-mono text-primary font-bold uppercase tracking-wider">
                                        {`> ${LOADING_MESSAGES[msgIndex]}`}
                                    </span>
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
